import { JSX, lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import LegacyJobRedirect from "../pages/digitalTwin/scenarios/LegacyJobRedirect";
import RequireAuth from "../components/auth/RequireAuth";
import { GuestOnly } from "../components/auth/GuestOnly";
import DashboardView from "@/pages/dashboardView/DashboardView";
import NotFound from "@/pages/notFound/NotFound";
import DummySkeleton from "@/layout/DummySkeleton";
import SmartRedirect from "@/components/auth/SmartRedirect";
import AuthCallbackPage from "@/auth/AuthCallbackPage";
import LoginRedirect from "@/auth/LoginRedirect";
import PendingApprovalPage from "@/auth/PendingApprovalPage";
import RequireModule from "@/auth/RequireModule";
import RequireRole from "@/auth/RequireRole";

// Every page is lazy-loaded, so a page's code (and its libraries) only
// downloads when that page is opened. Sign-in screens stay eager: they are
// small and are the first thing a signed-out user sees.
const AdminDashboard = lazy(
  () => import("../pages/adminDashboard/AdminDashboard"),
);
const AnalyzeStation = lazy(
  () => import("@/pages/analyzeStations/AnalyzeStations"),
);
const Profile = lazy(() => import("../pages/profile/Profile"));
const Settings = lazy(() => import("../pages/settings/Settings"));
const DigitalTwin = lazy(() => import("../pages/digitalTwin/DigitalTwin"));
const DoYouKnowYourRoad = lazy(
  () => import("../pages/digitalTwin/quiz/DoYouKnowYourRoad"),
);
const ScenariosLayout = lazy(
  () => import("../pages/digitalTwin/scenarios/ScenariosLayout"),
);
const RoadClosure = lazy(
  () => import("../pages/digitalTwin/closure/RoadClosure"),
);
const ScenarioJobs = lazy(
  () => import("../pages/digitalTwin/scenarios/JobsView"),
);
const ScenarioAnalysis = lazy(
  () => import("../pages/digitalTwin/scenarios/AnalysisView"),
);
const ScenarioDrafts = lazy(
  () => import("../pages/digitalTwin/scenarios/DraftsView"),
);
const ClosureSettingsPage = lazy(
  () => import("../pages/digitalTwin/closure/ClosureSettingsPage"),
);

const lazyPage = (page: JSX.Element) => (
  <Suspense fallback={<DummySkeleton />}>{page}</Suspense>
);
export interface AppRoute {
  path?: string;
  index?: boolean;
  element: JSX.Element;
  children?: AppRoute[];
}

export const appRoutes: AppRoute[] = [
  {
    path: "/",
    element: (
      <RequireAuth>
        <DashboardView />
      </RequireAuth>
    ),
    children: [
      // Pages: each one only for users whose access groups include it.
      {
        index: true,
        element: <SmartRedirect />,
      },

      {
        path: "digitaltwin",
        element: (
          <RequireModule module="digital-twin">
            {lazyPage(<DigitalTwin />)}
          </RequireModule>
        ),
      },
      {
        path: "digitaltwin/quiz",
        element: (
          <RequireModule module="quiz">
            {lazyPage(<DoYouKnowYourRoad />)}
          </RequireModule>
        ),
      },
      // Scenarios suite; rail items in pages/digitalTwin/scenarios/scenarioRailItems.ts
      {
        path: "digitaltwin/scenarios",
        element: (
          <RequireModule module="scenarios">
            {lazyPage(<ScenariosLayout />)}
          </RequireModule>
        ),
        children: [
          { index: true, element: <Navigate to="/digitaltwin/scenarios/closure" replace /> },
          // One editor, two kinds; the keys make each route a fresh page (no state carried over).
          { path: "closure", element: lazyPage(<RoadClosure key="closure" />) },
          // Speed limit (route id and path stay "zone30"); SPEED_LIMIT_PLAN.md.
          { path: "zone30", element: lazyPage(<RoadClosure key="zone30" kind="speed_limit" />) },
          { path: "jobs", element: lazyPage(<ScenarioJobs />) },
          { path: "drafts", element: lazyPage(<ScenarioDrafts />) },
          { path: "settings", element: lazyPage(<ClosureSettingsPage />) },
          { path: "analysis", element: lazyPage(<ScenarioAnalysis />) },
          { path: "analysis/:jobId", element: lazyPage(<ScenarioAnalysis />) },
          // Old job links showed the result on the closure page.
          { path: "jobs/:jobId", element: <LegacyJobRedirect /> },
        ],
      },
      // Old Road Closure URL.
      {
        path: "digitaltwin/closure",
        element: <Navigate to="/digitaltwin/scenarios/closure" replace />,
      },
      {
        path: "analyzeStation",
        element: (
          <RequireModule module="analyze-stations">
            {lazyPage(<AnalyzeStation />)}
          </RequireModule>
        ),
      },
      {
        path: "admin",
        element: (
          <RequireRole roles={["platform-admin"]}>
            {lazyPage(<AdminDashboard />)}
          </RequireRole>
        ),
      },

      // Non-protected pages (accessible to any logged-in user)
      { path: "profile", element: lazyPage(<Profile />) },
      { path: "settings", element: lazyPage(<Settings />) },
    ],
  },

  // The public landing page moved to mdf.lu (MDF_FRONTEND_LANDING_PAGE).
  { path: "/welcome", element: <Navigate to="/login" replace /> },
  // Sign-in happens on the MDF-branded AWS login page (password reset included).
  {
    path: "/login",
    element: (
      <GuestOnly>
        <LoginRedirect />
      </GuestOnly>
    ),
  },
  // Accounts are created by an admin (invitation); there is no self sign-up.
  { path: "/signup", element: <Navigate to="/login" replace /> },
  { path: "/recovery", element: <Navigate to="/login" replace /> },
  // The login page returns here with a one-time code.
  { path: "/auth/callback", element: <AuthCallbackPage /> },
  // Signed in, but no role yet.
  {
    path: "/pending",
    element: (
      <RequireAuth>
        <PendingApprovalPage />
      </RequireAuth>
    ),
  },

  // Unknown address: signed out → login, signed in → "not found".
  {
    path: "*",
    element: (
      <RequireAuth>
        <NotFound />
      </RequireAuth>
    ),
  },
];
